// Component: IndicatorsPanelTab.jsx
// Purpose: Live Analysis "Indicators" tab (board 50-6, package P8a; D53/D54/D55/D56 visual-gate
//          fix-ups) — a compact LIST of this strategy's chart-eligible nodes (same
//          ELIGIBLE_LAYER_NODE_TYPES filter as before) grouped into the Builder's OWN TWO-LEVEL
//          taxonomy: top-level `AccordionGroup`s (StrategyToolbox.jsx, named export) — Indicators
//          (sub-grouped by registry category, exactly like the Toolbox's own category grouping)
//          and Strategy Logic (sub-grouped Long Position / Short Position / Math & Logic, exactly
//          like the Toolbox's own layout, action→icon via the Toolbox's own `POSITIONS`/
//          `MATH_LOGIC_ICONS` named exports — D56: "must mirror the Toolbox's two-level
//          taxonomy", never a second hand-maintained grouping). Each row: an IconChip-style icon
//          → smart label → a muted "→ <feeds>" subtitle (first outgoing edge, disambiguates
//          duplicate comparators) → override dot + eye toggle; the line-colour swatch survives
//          only for indicator/math rows (where a line is actually drawn); the old type chip is
//          gone. ONE properties view for the SELECTED node — a "← Back to list" header (D55
//          master/detail swap, keeps the pane on-screen for a long list) + the same icon/label/
//          subtitle + the Builder's `ChartStylingSection` rendered AS-IS, bare. Selection is
//          SHARED with the left RDWatchDashboard via `selectedNodeId`/`onSelectNode` (lifted to
//          LiveAnalysisTab, mirroring RDWatchDashboard's own onItemClick/selectedNodeId contract
//          — RDWatchDashboard.jsx:260-344). Save/Reset/Clear-saved toolbar, dirty indicator,
//          428/412 handling, and the `effectiveNodesForEditor` baseline are UNCHANGED — this is a
//          presentation restructure, not a data change. The merge chokepoint
//          (`patchNodeData`/`applyVisualOverrides`/`diffPatch`) is imported from
//          chartDataDerivation.js — ONE definition, never copied (D35/D40, 02-design.md §C).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiEye, FiEyeOff, FiSearch, FiActivity, FiGitCommit, FiArrowLeft } from 'react-icons/fi';
import {
  ELIGIBLE_LAYER_NODE_TYPES,
  VISUAL_DATA_KEYS,
  applyVisualOverrides,
  defaultOutputStyles,
  hasStylingOverride,
} from '../../Strategies/Modern/StrategyBuilder/utils/chartDataDerivation';
import {
  AUTHORING_GROUP,
  groupAuthoringItems,
  MATH_LOGIC_TYPES,
  resolveDisplayName,
} from '../../Strategies/Modern/StrategyBuilder/utils/nodeUtils';
import {
  isNodeShownOnChart,
  getNodeAccent,
  buildNodeVisualTogglePatch,
} from '../../Strategies/Modern/StrategyBuilder/config/nodeVisualConfig';
import ChartStylingSection from '../../Strategies/Modern/StrategyBuilder/components/ChartStylingSection';
import IndicatorAboutSection, { IndicatorConfigReadonly } from '../../Strategies/Modern/StrategyBuilder/components/IndicatorAboutSection';
import { AccordionGroup, SubgroupHeader, POSITIONS, MATH_LOGIC_ICONS } from '../../Strategies/Modern/StrategyBuilder/components/StrategyToolbox';
import ConfirmDialog from '../../../components/trading/ConfirmDialog';
import { VisualOverridesNotice } from '../../../hooks/useVisualOverrides';

//! D56 — action→icon lookup DERIVED from the Toolbox's own `POSITIONS` export, never a second
//! hand-maintained table. long/short share the same icon per action (sl/tp/time_exit) in
//! `POSITIONS` itself, so folding both arrays in is safe and also picks up the side-specific
//! enter_long/enter_short/exit_long/exit_short icons.
const ICON_BY_ACTION = Object.fromEntries(
  [...POSITIONS.long, ...POSITIONS.short].map((item) => [item.data.action, item.icon]),
);
//--> D56 — first outgoing edge's target, smart-labelled — disambiguates duplicate comparators
//*   ("RSI > 70" vs "RSI > 70" feeding two different signals) without inventing a second label
//*   authority; `resolveDisplayName` is the SAME one every row/header already calls.
const resolveFeedsLabel = (node, versionNodes, edges, record) => {
  const edge = (edges || []).find((e) => e.source === node.id);
  if (!edge) return null;
  const target = (versionNodes || []).find((n) => n.id === edge.target);
  if (!target) return null;
  return resolveDisplayName(target, versionNodes, edges, record?.[target.id], record);
};

//--> D56 — row/header icon: Math & Logic types from the Toolbox's own `MATH_LOGIC_ICONS`;
//*   signal/position_trigger/time_window from the Toolbox's own `ICON_BY_ACTION` (derived above
//*   from `POSITIONS`), falling back to `FiActivity` for an action `POSITIONS` doesn't carry
//*   (never crashes on an unmapped action); plain `indicator` nodes use `FiActivity`, exactly
//*   what the Toolbox itself renders for every registry indicator item
//*   (StrategyToolbox.jsx:316, `icon: FiActivity`).
const resolveRowIcon = (node) => {
  if (MATH_LOGIC_TYPES.includes(node.type)) return MATH_LOGIC_ICONS[node.type] || FiActivity;
  if (node.data?.action && ICON_BY_ACTION[node.data.action]) return ICON_BY_ACTION[node.data.action];
  return FiActivity;
};

//--> D54 — resolve the swatch's line colour + dash pattern from the SAME fields
//*   ChartStylingSection's own OutputStylePicker reads (ChartStylingSection.jsx:148,164,173):
//*   top-level `color` → `visual_settings.color` → `getNodeAccent` fallback. D56: swatch now
//*   renders ONLY for indicator/math rows (where a line is actually drawn).
const SWATCH_ELIGIBLE_TYPES = ['indicator', 'math'];
const resolveSwatch = (nodeType, nodeData) => {
  const color = nodeData?.color || nodeData?.visual_settings?.color || getNodeAccent(nodeType, nodeData);
  const lineStyle = nodeData?.lineStyle;
  const borderStyle = lineStyle === 1 ? 'dotted' : lineStyle === 2 ? 'dashed' : 'solid';
  return { color, borderStyle };
};

//* 50-8/50-21 — the autosave store, the save-payload builder and the display adapter live in ONE hook module
//  (`hooks/useVisualOverrides.js`, the only module allowed to import `saveVisualOverrides`). This panel is a
//  pure consumer of the `store` LiveAnalysisTab mounts from it.

//--> D20 — the overridden-badge predicate lives beside VISUAL_DATA_KEYS (chartDataDerivation) so the
//    Builder's styling controls share it without importing this page; re-exported for existing callers.
export { hasStylingOverride };

//--> Shared icon chip + swatch + label/subtitle, reused by the list row AND the detail header
//*   (D54/D55/D56) — ONE rendering of "what node is this" rather than two near-copies. Mirrors
//*   the Toolbox's own `IconChip` classes (StrategyToolbox.jsx:78-89: accent-tinted background +
//*   accent icon) — `IconChip` itself isn't exported (only `AccordionGroup`/`SectionHeader`/
//*   `POSITIONS`/`MATH_LOGIC_ICONS` are, D54/D56), so the identical classes are reproduced here.
const NodeIdentity = ({ versionNode, effectiveNode, label, versionNodes, edges, record }) => {
  const accent = getNodeAccent(versionNode.type, effectiveNode.data);
  const Icon = resolveRowIcon(versionNode);
  const showSwatch = SWATCH_ELIGIBLE_TYPES.includes(versionNode.type);
  const { color, borderStyle } = resolveSwatch(versionNode.type, effectiveNode.data);
  const swatchStyle = borderStyle === 'solid'
    ? { backgroundColor: color }
    : { backgroundColor: 'transparent', borderTop: `2px ${borderStyle} ${color}` };
  const feedsLabel = resolveFeedsLabel(versionNode, versionNodes, edges, record);
  return (
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <div
        className="p-1.5 rounded-lg shrink-0"
        style={{ color: accent, background: 'color-mix(in oklch, currentColor 12%, transparent)' }}
      >
        <Icon size={14} />
      </div>
      {showSwatch && (
        <span
          data-testid={`row-swatch-${versionNode.id}`}
          aria-hidden="true"
          className="shrink-0 rounded-full w-4 h-0.5"
          style={swatchStyle}
        />
      )}
      <div className="min-w-0 flex-1">
        <span className="text-xs font-bold text-fg-2 truncate block">{label}</span>
        {feedsLabel && (
          <span className="text-[10px] text-fg-3 truncate block" data-testid={`row-feeds-${versionNode.id}`}>
            → {feedsLabel}
          </span>
        )}
      </div>
    </div>
  );
};

/*>>> LIST ROW (D53/D54/D56) — icon + swatch (indicator/math only) + smart label + feeds subtitle
      + visibility eye + overridden dot + selected state. NO ChartStylingSection here — that
      lives in the ONE detail view (D55) selecting a row swaps to (the D53 complaint: "we just
      added everything in a row, which is very confusing"). <<<*/
const IndicatorListRow = ({
  versionNode,
  versionNodes,
  edges,
  overridesSaved,
  draftOverrides,
  record,
  isSelected,
  onSelect,
  onToggleVisible,
  disabled,
}) => {
  //* §H — each row computes its own displayed node fresh every render, no shadow-ref state.
  const effectiveNode = useMemo(
    () => applyVisualOverrides([versionNode], overridesSaved, draftOverrides)[0],
    [versionNode, overridesSaved, draftOverrides],
  );
  const label = resolveDisplayName(versionNode, versionNodes, edges, record?.[versionNode.id], record);
  const hasOverride = hasStylingOverride(record?.[versionNode.id]);
  const isVisible = isNodeShownOnChart(versionNode.type, effectiveNode.data); //! reuses the ONE
  //  "is chart-visible" rule (nodeVisualConfig.js) — same authority ChartStylingSection's own
  //  header eye reads (ChartStylingSection.jsx:1303-1324, D53 "reuse the existing helper").

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`indicator-row-${versionNode.id}`}
      aria-current={isSelected ? 'true' : 'false'}
      onClick={() => onSelect(versionNode.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(versionNode.id); }}
      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg border cursor-pointer transition-colors ${
        isSelected
          ? 'border-accent bg-[color-mix(in_oklch,var(--color-accent)_10%,transparent)]'
          : 'border-[var(--divider)] hover:border-[var(--border)]'
      }`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {hasOverride && (
          //! decorative only — no aria-label: keeps the row's ONE aria-label (the eye toggle)
          //! as the sole accessible/interactive element existing tests and screen readers key off.
          <span
            data-testid={`override-dot-${versionNode.id}`}
            title="Overridden"
            aria-hidden="true"
            className="w-1.5 h-1.5 rounded-full bg-[var(--color-accent)] shrink-0"
          />
        )}
        <NodeIdentity versionNode={versionNode} effectiveNode={effectiveNode} label={label} versionNodes={versionNodes} edges={edges} record={record} />
      </div>
      <button
        type="button"
        data-testid={`row-visibility-${versionNode.id}`}
        aria-label={isVisible ? 'Hide from Chart' : 'Show on Chart'}
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          onToggleVisible(versionNode.id, effectiveNode.data, versionNode.type);
        }}
        className={`p-1 rounded shrink-0 disabled:opacity-40 ${
          isVisible ? 'text-[var(--color-accent)]' : 'text-fg-3 hover:text-fg-2'
        }`}
      >
        {isVisible ? <FiEye size={13} /> : <FiEyeOff size={13} />}
      </button>
    </div>
  );
};

/*>>> PROPERTIES / DETAIL VIEW (D53/D54/D55/D56/D57) — ONE mounted instance, for the currently
      selected node only; REPLACES the list (master/detail swap, D55 — "Chart Styling & Overlay
      was below the 20-row list, off-screen"). A compact "← Back to list" header row (left) +
      per-node "Restore default" (D57, renamed from "Reset" — there is no draft-vs-saved
      distinction left to "reset" FROM, autosave means the draft IS the saved state within
      ≤500ms; the real action is reverting to the version's own default) (right), then the SAME
      icon/swatch/label/feeds identity the row shows, then the Builder's `ChartStylingSection`
      rendered bare (D54 — no second card/border; its OWN "Chart Styling & Overlay" header with
      eye + chevron IS the Builder's exact panel). <<<*/
const IndicatorPropertiesPane = ({
  versionNode,
  versionNodes,
  edges,
  overridesSaved,
  draftOverrides,
  record,
  effectiveNodesForEditor,
  adapterUpdateNodeData,
  onRestoreNode,
  onBack,
  disabled,
  stylingLocked = false,
  allIndicatorMetadata = undefined,
}) => {
  const effectiveNode = useMemo(
    () => applyVisualOverrides([versionNode], overridesSaved, draftOverrides)[0],
    [versionNode, overridesSaved, draftOverrides],
  );
  const label = resolveDisplayName(versionNode, versionNodes, edges, record?.[versionNode.id], record);
  const hasSaved = versionNode.id in (overridesSaved || {});
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleRestoreClick = () => {
    if (hasSaved) {
      setConfirmOpen(true);
    } else {
      onRestoreNode(versionNode.id);
    }
  };

  return (
    <div className="space-y-2" data-testid="indicator-properties-pane">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid="detail-back"
          onClick={onBack}
          className="flex items-center gap-1 text-[10px] font-bold uppercase text-fg-3 hover:text-fg-1 shrink-0"
        >
          <FiArrowLeft size={12} /> Back to list
        </button>
        <button
          type="button"
          data-testid={`row-restore-${versionNode.id}`}
          onClick={handleRestoreClick}
          disabled={disabled}
          className="text-[10px] font-bold uppercase text-fg-3 hover:text-loss disabled:opacity-40 shrink-0"
        >
          Restore default
        </button>
      </div>
      <NodeIdentity versionNode={versionNode} effectiveNode={effectiveNode} label={label} versionNodes={versionNodes} edges={edges} record={record} />
      <div
        data-testid="styling-lock"
        aria-disabled={stylingLocked ? 'true' : undefined}
        className={stylingLocked ? 'pointer-events-none opacity-60' : undefined}
      >
        <ChartStylingSection
          nodeType={versionNode.type}
          nodeData={effectiveNode.data}
          nodeId={versionNode.id}
          updateNodeData={adapterUpdateNodeData}
          allNodes={effectiveNodesForEditor}
          defaultOutputStyles={defaultOutputStyles(effectiveNode.data?.indicator, allIndicatorMetadata, effectiveNode.data?.visual_settings, effectiveNode.data?.outputs)}
        />
      </div>
      {/* 142-49 slice 6: reference only — styling stays first (the one editable thing here), then the
          read-only running configuration, then About; both collapsed, both from the registry docs */}
      {versionNode.type === 'indicator' && (
        <>
          <IndicatorConfigReadonly key={`cfg-${versionNode.id}`} nodeId={versionNode.id} nodeData={effectiveNode.data} allIndicatorMetadata={allIndicatorMetadata} />
          <IndicatorAboutSection key={`about-${versionNode.id}`} nodeId={versionNode.id} nodeData={effectiveNode.data} allIndicatorMetadata={allIndicatorMetadata} displayName={label} />
        </>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title="Restore default styling?"
        message="This removes the saved visual override for this indicator, including its authored name and notes. This cannot be undone."
        confirmLabel="Restore default"
        danger
        onConfirm={() => {
          setConfirmOpen(false);
          onRestoreNode(versionNode.id);
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};

/*>>> MAIN COMPONENT <<<*/
//! D53 — `selectedNodeId`/`onSelectNode` are OPTIONAL controlled-selection props mirroring
//! RDWatchDashboard's onItemClick/selectedNodeId contract (RDWatchDashboard.jsx:260-344).
//! LiveAnalysisTab lifts and passes these so the left dashboard and this list share ONE
//! selection; a caller that omits them (none exist today, but kept zero-diff-safe) falls back
//! to local state.
const IndicatorsPanelTab = ({ store, versionNodes, edges, selectedNodeId = undefined, onSelectNode = undefined, categoryByIndicator = {}, allIndicatorMetadata = undefined }) => {
  const {
    overridesSaved,
    draftOverrides,
    effectiveOverrides,
    updateNodeDisplay,
    canEdit,
    loadFailed,
    writeInFlight,
    writeFailed,
    isSaving,
    bodyTooLarge,
    restoring,
    reload,
    flushAutosave,
    restoreDefaults,
    restoreNode,
  } = store;

  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);

  const eligibleNodes = useMemo(
    () => (versionNodes || []).filter((n) => ELIGIBLE_LAYER_NODE_TYPES.includes(n.type)),
    [versionNodes],
  );

  //* §H — EFFECTIVE node array (version ⊕ saved ⊕ draft), passed as `allNodes` so cross-node
  //  writers (LevelDisplayControl) spread the OTHER node's already-effective data (D40/V3-N2).
  const effectiveNodesForEditor = useMemo(
    () => applyVisualOverrides(versionNodes, overridesSaved, draftOverrides),
    [versionNodes, overridesSaved, draftOverrides],
  );

  //--> every control + the eye toggle write through the store's ONE display adapter
  //    (`updateNodeDisplay`: diff-against-baseline, D40/§H, then the ≤500 ms autosave, D57) — no
  //    second write path lives in this panel.
  const adapterUpdateNodeData = updateNodeDisplay;

  const writesDisabled = writeInFlight || loadFailed || !canEdit;
  //! A-3/A-9 — the styling controls are locked (disabled look) when the record failed to load, is
  //  read-only, or a Restore PUT is in flight; the store also refuses the writes (the real guard).
  const stylingLocked = loadFailed || !canEdit || !!restoring;

  //* D53 — selection: controlled (LiveAnalysisTab, shared with RDWatchDashboard) when
  //* `selectedNodeId`/`onSelectNode` are passed, else local. Default selection = first
  //* eligible node (R5.1/R5.3 "we don't know which one we are selecting").
  const [internalSelectedNodeId, setInternalSelectedNodeId] = useState(null);
  const isControlled = selectedNodeId !== undefined;
  const effectiveSelectedId = isControlled ? selectedNodeId : internalSelectedNodeId;

  //* D55 — master/detail swap: `detailOpen` decides whether the LIST or the ONE properties
  //* view renders (the Chart Styling & Overlay pane was below the 20-row list, off-screen).
  //* Default on first open = the list — the auto-default-selection below never opens it; only a
  //* genuine user action (a list-row click, or a dashboard-card click landing here via the
  //* CONTROLLED `selectedNodeId` prop changing to a value this component didn't itself pick)
  //* does. `lastSelfSelectedIdRef` is how we tell "I picked this" apart from "the dashboard did".
  const [detailOpen, setDetailOpen] = useState(false);
  const lastSelfSelectedIdRef = useRef(null);

  const handleSelect = useCallback((nodeId, { openDetail = true } = {}) => {
    lastSelfSelectedIdRef.current = nodeId;
    if (onSelectNode) onSelectNode(nodeId);
    else setInternalSelectedNodeId(nodeId);
    if (openDetail) setDetailOpen(true);
  }, [onSelectNode]);

  useEffect(() => {
    if (!effectiveSelectedId && eligibleNodes.length > 0) {
      handleSelect(eligibleNodes[0].id, { openDetail: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSelectedId, eligibleNodes]);

  //--> A dashboard-card click updates the CONTROLLED `selectedNodeId` prop directly (never
  //*   through this component's own `handleSelect`) — detect that external change here and
  //*   open the detail view for it too (D55: "by a list row OR a dashboard card").
  useEffect(() => {
    if (effectiveSelectedId && effectiveSelectedId !== lastSelfSelectedIdRef.current) {
      lastSelfSelectedIdRef.current = effectiveSelectedId;
      setDetailOpen(true);
    }
  }, [effectiveSelectedId]);

  const handleBack = useCallback(() => setDetailOpen(false), []);

  //--> Esc closes the detail view back to the list, keeping the selection (D55).
  useEffect(() => {
    if (!detailOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setDetailOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [detailOpen]);

  const selectedVersionNode = useMemo(
    () => eligibleNodes.find((n) => n.id === effectiveSelectedId) || null,
    [eligibleNodes, effectiveSelectedId],
  );

  //--> Eye toggle (D53) — the patch comes from the ONE shared toggle helper (R1-T3, same shape as
  //*   every Builder eye) and is written through the ONE adapter — never a second write path.
  const handleToggleVisible = useCallback((nodeId, nodeData, nodeType) => {
    adapterUpdateNodeData(nodeId, buildNodeVisualTogglePatch(nodeType, nodeData, 'visibility'));
  }, [adapterUpdateNodeData]);

  //* D54 — search box, styled exactly like the Toolbox's "Search components…" box, filters rows
  //* by smart label (case-insensitive).
  const [searchQuery, setSearchQuery] = useState('');
  const lowerQuery = searchQuery.trim().toLowerCase();
  const filteredNodes = useMemo(() => {
    if (!lowerQuery) return eligibleNodes;
    return eligibleNodes.filter((n) => resolveDisplayName(n, versionNodes, edges, effectiveOverrides?.[n.id], effectiveOverrides).toLowerCase().includes(lowerQuery));
  }, [eligibleNodes, versionNodes, edges, lowerQuery, effectiveOverrides]);

  const groupedRows = useMemo(() => {
    const subgroups = groupAuthoringItems(filteredNodes, categoryByIndicator);
    return [...new Set(subgroups.map((subgroup) => subgroup.group))].map((group) => {
      const groupSubgroups = subgroups.filter((subgroup) => subgroup.group === group);
      return {
        key: group.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        title: group,
        icon: group === AUTHORING_GROUP.INDICATORS ? FiActivity : FiGitCommit,
        nodes: groupSubgroups.flatMap((subgroup) => subgroup.nodes),
        subgroups: groupSubgroups,
      };
    });
  }, [filteredNodes, categoryByIndicator]);

  return (
    <div className="space-y-4" data-testid="indicators-panel-tab">
      {/* A-2 — the ONE shared notice (too large to save / record unavailable), same element as the Builder */}
      <VisualOverridesNotice store={store} />
      {/*>>> TOOLBAR (D57 autosave) — text style aligned with the Toolbox's SectionHeader
          (font-black, uppercase, tracking-widest) while keeping the functional accent/loss/fg-3
          colours. Save/Reset/dirty-indicator are GONE — every change autosaves; the status text
          is the one signal the user needs. <<<*/}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {loadFailed && (
            <button type="button" data-testid="toolbar-retry" onClick={reload} className="text-[10px] font-black uppercase tracking-widest text-loss">
              Retry
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!loadFailed && canEdit && (
            bodyTooLarge ? (
              //! A-2 — a record the server refused as too large is NOT saved: never show "Saved".
              <span data-testid="save-status" className="text-[10px] font-black uppercase tracking-widest text-loss">
                Not saved
              </span>
            ) : writeFailed ? (
              <span data-testid="save-status" className="text-[10px] font-black uppercase tracking-widest text-loss flex items-center gap-1">
                Not saved
                <button
                  type="button"
                  data-testid="save-status-retry"
                  onClick={() => flushAutosave()}
                  className="underline hover:text-fg-1"
                >
                  retry
                </button>
              </span>
            ) : (
              <span data-testid="save-status" className="text-[10px] font-black uppercase tracking-widest text-fg-3">
                {isSaving ? 'Saving…' : 'Saved'}
              </span>
            )
          )}
          <button
            type="button"
            data-testid="toolbar-restore-defaults"
            onClick={() => setRestoreConfirmOpen(true)}
            disabled={writesDisabled}
            className="text-[10px] font-black uppercase tracking-widest text-loss disabled:opacity-40"
          >
            Restore defaults
          </button>
        </div>
      </div>

      {/*>>> MASTER/DETAIL SWAP (D55) — the list and the ONE properties view are mutually
          exclusive; never both, never the pane pushed below a long list. <<<*/}
      {detailOpen && selectedVersionNode ? (
        <IndicatorPropertiesPane
          versionNode={selectedVersionNode}
          versionNodes={versionNodes}
          edges={edges}
          overridesSaved={overridesSaved}
          draftOverrides={draftOverrides}
          record={effectiveOverrides}
          effectiveNodesForEditor={effectiveNodesForEditor}
          adapterUpdateNodeData={adapterUpdateNodeData}
          onRestoreNode={restoreNode}
          onBack={handleBack}
          disabled={writesDisabled}
          stylingLocked={stylingLocked}
          allIndicatorMetadata={allIndicatorMetadata}
        />
      ) : detailOpen ? (
        <div className="space-y-2" data-testid="indicator-no-properties">
          <button
            type="button"
            data-testid="detail-back"
            onClick={handleBack}
            className="flex items-center gap-1 text-[10px] font-bold uppercase text-fg-3 hover:text-fg-1"
          >
            <FiArrowLeft size={12} /> Back to list
          </button>
          <p className="text-xs text-fg-3 italic p-2">This item has no chart styling options.</p>
        </div>
      ) : (
        <>
          {/*>>> SEARCH (D54) — copied verbatim from StrategyToolbox's "Search components…" box. <<<*/}
          <div className="relative group">
            <FiSearch className="absolute left-3 top-3 text-fg-3 group-focus-within:text-[var(--color-accent)] transition-colors" />
            <input
              type="text"
              data-testid="indicator-search"
              placeholder="Search components..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-bg-3 rounded-xl text-sm font-medium text-fg-1 placeholder:text-fg-3 border border-transparent focus:ring-2 focus:border-transparent outline-none transition-all shadow-sm"
              style={{ '--tw-ring-color': 'color-mix(in oklch, var(--color-accent) 30%, transparent)' }}
            />
          </div>

          {/*>>> LIST (D53/D54/D56) — grouped into the Builder's own AccordionGroups, each
              sub-grouped exactly like the Toolbox's two-level taxonomy; one compact row per
              eligible node, never its full controls. <<<*/}
          <div data-testid="indicator-list">
            {groupedRows.filter((g) => g.nodes.length > 0).map((g) => (
              <AccordionGroup
                key={g.key}
                title={g.title}
                icon={g.icon}
                defaultOpen
                forceOpen={!!lowerQuery}
                persistenceKey={`live_${g.key}`}
              >
                <div className="px-4 pt-4 space-y-4">
                  {g.subgroups.map((sg) => (
                    <div
                      key={sg.id}
                      data-testid="indicators-panel-authoring-subgroup"
                      data-authoring-group-id={sg.id}
                    >
                      {sg.title && <SubgroupHeader title={sg.title} />}
                      <div className="space-y-1">
                        {sg.nodes.map((versionNode) => (
                          <div key={versionNode.id} data-authoring-item-id={versionNode.id}>
                            <IndicatorListRow
                              versionNode={versionNode}
                              versionNodes={versionNodes}
                              edges={edges}
                              overridesSaved={overridesSaved}
                              draftOverrides={draftOverrides}
                              record={effectiveOverrides}
                              isSelected={versionNode.id === effectiveSelectedId}
                              onSelect={handleSelect}
                              onToggleVisible={handleToggleVisible}
                              disabled={writesDisabled}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionGroup>
            ))}
            {lowerQuery && groupedRows.every((g) => g.nodes.length === 0) && (
              <p className="px-4 py-3 text-xs text-fg-3 italic">No matches</p>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={restoreConfirmOpen}
        title="Restore every default?"
        message="This removes every saved visual override for this strategy, including authored condition names and notes. This cannot be undone."
        confirmLabel="Restore defaults"
        danger
        onConfirm={() => {
          setRestoreConfirmOpen(false);
          restoreDefaults();
        }}
        onCancel={() => setRestoreConfirmOpen(false)}
      />
    </div>
  );
};

//! P2 render boundary (design §E) — memoized like the other three panel bodies.
export default React.memo(IndicatorsPanelTab);
